import { createI18n } from 'vue-i18n'
import { VeeValidate } from '@/plugins/veeValidate'
import authFR from '@/assets/translations/fr/auth.json'
import routesFR from '@/assets/translations/fr/routes.json'
import myMapFR from '@/assets/translations/fr/myMap.json'
import atlasFR from '@/assets/translations/fr/atlas.json'
import shareFR from '@/assets/translations/fr/share.json'
import geocodingFR from '@/assets/translations/fr/geocoding.json'
import authEN from '@/assets/translations/en/auth.json'
import routesEN from '@/assets/translations/en/routes.json'
import myMapEN from '@/assets/translations/en/myMap.json'
import atlasEN from '@/assets/translations/en/atlas.json'
import shareEN from '@/assets/translations/en/share.json'
import geocodingEN from '@/assets/translations/en/geocoding.json'
import commonFR from '@/assets/translations/fr/common.json'
import commonEN from '@/assets/translations/en/common.json'
import adminFr from '@/assets/translations/fr/admin.json'
import adminEn from '@/assets/translations/en/admin.json'
import projectEn from '@/assets/translations/en/projects.json'
import projectFr from '@/assets/translations/fr/projects.json'
import homeEn from '@/assets/translations/en/home.json'
import homeFr from '@/assets/translations/fr/home.json'
import actorEn from '@/assets/translations/en/actors.json'
import actorFr from '@/assets/translations/fr/actors.json'
import resourcesFr from '@/assets/translations/fr/resources.json'
import resourcesEn from '@/assets/translations/en/resources.json'
import myMapFr from '@/assets/translations/fr/myMap.json'
import myMapEn from '@/assets/translations/en/myMap.json'
import geodataFr from '@/assets/translations/fr/geodata.json'
import geodataEn from '@/assets/translations/en/geodata.json'
import infrastructureFr from '@/assets/translations/fr/infrastructure.json'
import infrastructureEn from '@/assets/translations/en/infrastructure.json'
import resumeStatistiqueFr from '@/assets/translations/fr/resumeStatistique.json'
import resumeStatistiqueEn from '@/assets/translations/en/resumeStatistique.json'
import geoportalFr from '@/assets/translations/fr/geoportal.json'
import geoportalEn from '@/assets/translations/en/geoportal.json'
import naturalLanguageQueryFr from '@/assets/translations/fr/naturalLanguageQuery.json'
import naturalLanguageQueryEn from '@/assets/translations/en/naturalLanguageQuery.json'
import dashboardFr from '@/assets/translations/fr/dashboard.json'
import dashboardEn from '@/assets/translations/en/dashboard.json'

/**
 * Langues servies par l'interface. Le français reste la langue de repli : la plateforme est
 * camerounaise et les données métier (noms de quartiers, libellés de typologies) n'existent
 * qu'en français côté base.
 */
export const LOCALES_DISPONIBLES = ['fr', 'en'] as const

export type LocaleDisponible = (typeof LOCALES_DISPONIBLES)[number]

export const LOCALE_PAR_DEFAUT: LocaleDisponible = 'fr'

/** Clé de stockage du choix de langue, préfixée comme les autres entrées de l'application. */
const CLE_LOCALE = 'app:locale'

export function estLocaleDisponible(valeur: unknown): valeur is LocaleDisponible {
  return LOCALES_DISPONIBLES.includes(valeur as LocaleDisponible)
}

/**
 * Langue retenue au démarrage : le dernier choix de l'utilisateur, sinon le français.
 *
 * La lecture est protégée : en navigation privée ou stockage refusé, `localStorage` lève au lieu
 * de renvoyer null, et l'application ne doit pas s'arrêter là.
 */
function localeInitiale(): LocaleDisponible {
  try {
    const memorisee = window.localStorage.getItem(CLE_LOCALE)
    if (estLocaleDisponible(memorisee)) return memorisee
  } catch {
    // Stockage indisponible : on s'en tient à la langue par défaut.
  }

  return LOCALE_PAR_DEFAUT
}

export const i18nInstance = createI18n({
  legacy: false,
  locale: localeInitiale(),
  fallbackLocale: LOCALE_PAR_DEFAUT,
  messages: {
    fr: {
      ...authFR,
      ...routesFR,
      ...myMapFR,
      ...atlasFR,
      ...shareFR,
      ...geocodingFR,
      ...commonFR,
      ...adminFr,
      ...projectFr,
      ...homeFr,
      ...actorFr,
      ...myMapFr,
      ...resourcesFr,
      ...geodataFr,
      ...infrastructureFr,
      ...resumeStatistiqueFr,
      ...geoportalFr,
      ...naturalLanguageQueryFr,
      ...dashboardFr,
    },
    en: {
      ...authEN,
      ...routesEN,
      ...myMapEN,
      ...atlasEN,
      ...shareEN,
      ...geocodingEN,
      ...commonEN,
      ...adminEn,
      ...projectEn,
      ...homeEn,
      ...myMapEn,
      ...actorEn,
      ...resourcesEn,
      ...geodataEn,
      ...infrastructureEn,
      ...resumeStatistiqueEn,
      ...geoportalEn,
      ...naturalLanguageQueryEn,
      ...dashboardEn,
    },
  },
})

export const i18n = i18nInstance.global //Workaround for translate text outside components

/**
 * Point d'entrée unique du changement de langue.
 *
 * Trois choses doivent bouger ensemble, et c'est pourquoi elles sont réunies ici plutôt que
 * laissées au composant qui déclenche le changement : les libellés de l'interface, les messages de
 * validation des formulaires (vee-validate tient son propre dictionnaire), et l'attribut `lang` du
 * document, dont dépendent les lecteurs d'écran et la coupure des mots.
 */
export function appliquerLocale(locale: LocaleDisponible): void {
  i18n.locale.value = locale
  VeeValidate.setLocale(locale)
  document.documentElement.setAttribute('lang', locale)

  try {
    window.localStorage.setItem(CLE_LOCALE, locale)
  } catch {
    // Choix non mémorisé : la langue reste appliquée pour la session en cours.
  }
}

// createI18n ne touche pas au document : l'attribut lang est posé ici pour la langue restaurée.
// vee-validate, lui, n'est configuré qu'ensuite et reçoit la langue depuis registerPlugins.
document.documentElement.setAttribute('lang', i18n.locale.value)
